import React, { useMemo, useState } from 'react';
import { Scissors, Plus, Search } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import CarveQuestionPicker from '@/pages/_shared/CarveQuestionPicker';
import type { AvailableQuestion } from '@/services/PagesServices/AssessmentManagementService';
import './CarveAssessment.css';

interface CarveAssessmentProps {
  onCarve?: (questions: AvailableQuestion[]) => void;
}

const CarveAssessment: React.FC<CarveAssessmentProps> = ({ onCarve }) => {
  const { carveAssessmentSources } = useAssessmentManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [pickerSource, setPickerSource] = useState<{ id: number; name: string } | null>(null);

  const filteredSources = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return carveAssessmentSources ?? [];
    return (carveAssessmentSources ?? []).filter((a) =>
      (a.name ?? '').toLowerCase().includes(term)
    );
  }, [carveAssessmentSources, searchTerm]);

  return (
    <div className="trainer-assessment-carve-assessment">
      <div className="trainer-assessment-carve-header">
        <div className="trainer-assessment-carve-icon-wrapper">
          <Scissors size={48} />
        </div>
        <div className="trainer-assessment-carve-title-section">
          <h3>Carve Assessment</h3>
          <p>Create customized assessments by selecting specific questions from existing assessments</p>
        </div>
      </div>

      <div className="trainer-assessment-carve-content">
        <div className="trainer-assessment-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search existing assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="trainer-assessment-assessment-source-list">
          {filteredSources.length === 0 ? (
            <div className="trainer-assessment-empty-state">No assessments match your search.</div>
          ) : null}
          {filteredSources.map((assessment) => (
            <div key={assessment.id} className="trainer-assessment-source-item">
              <div className="trainer-assessment-source-info">
                <h4>{assessment.name}</h4>
                <span>{assessment.questions} questions available</span>
              </div>
              <button
                type="button"
                className="trainer-assessment-select-btn"
                disabled={!assessment.questions}
                onClick={() => setPickerSource({ id: assessment.id, name: assessment.name })}
                title={assessment.questions ? 'Pick questions from this assessment' : 'This assessment has no questions to carve'}
              >
                <Plus size={16} />
                Select Questions
              </button>
            </div>
          ))}
        </div>
      </div>

      {pickerSource && (
        <CarveQuestionPicker
          sourceId={pickerSource.id}
          sourceName={pickerSource.name}
          onCancel={() => setPickerSource(null)}
          onConfirm={(chosen) => {
            setPickerSource(null);
            onCarve?.(chosen);
          }}
        />
      )}
    </div>
  );
};

export default CarveAssessment;
